import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from "../../utils/Enums";
import { apiClient } from "../../utils/ApiClient";
import User from "../../models/User";
import {ModifyUserFormProps} from "../../components/users/ModifyUserScreen";
import {NewUserFormProps} from "../../components/users/NewUserScreen";

interface UsersState {
    users: User[];
    statuses: User[],
    currentUser?: User | null;
    getUsersStatus: RequestStatus;
    getCurrentUserStatus: RequestStatus;
    createUserStatus: RequestStatus;
    updateUserStatus: RequestStatus;
    deleteUserStatus: RequestStatus;
    errors: string[];
}

const initialState: UsersState = {
    users: [],
    statuses: [],
    currentUser: null,
    getUsersStatus: RequestStatus.Idle,
    getCurrentUserStatus: RequestStatus.Idle,
    createUserStatus: RequestStatus.Idle,
    updateUserStatus: RequestStatus.Idle,
    deleteUserStatus: RequestStatus.Idle,
    errors: [],
};

export const getUsers = createAsyncThunk(
    'users/getUsers',
    async (_, { rejectWithValue }) => {
        const response = await apiClient.get('users');
        return response.data;
    }
);

export const createUser = createAsyncThunk(
    'users/createUser',
    async (data: NewUserFormProps, { rejectWithValue }) => {
        const response = await apiClient.post('users', data);
        return response.data;
    }
);

export const updateUser = createAsyncThunk(
    'users/updateUser',
    async ({ id, userData }: { id: string, userData: ModifyUserFormProps }, { rejectWithValue }) => {
        const response = await apiClient.put(`users/${id}`, userData);
        return response.data;
    }
);

export const updateStatus = createAsyncThunk(
    'users/updateStatus',
    async ({ id, userData }: { id: string, userData: any }, { rejectWithValue }) => {
        const response = await apiClient.put(`user/${id}/status`, userData);
        return response.data;
    }
);

export const deleteUser = createAsyncThunk(
    'users/deleteUser',
    async (id: string, { rejectWithValue }) => {
        const response = await apiClient.delete(`users/${id}`);
        return response.data;
    }
);

export const getCurrentUser = createAsyncThunk(
    'users/getCurrentUser',
    async (id: string, { rejectWithValue }) => {
        const response = await apiClient.get(`users/${id}`);
        return response.data;
    }
);

export const getStatuses = createAsyncThunk(
    'users/getStatuses',
    async (_, { rejectWithValue }) => {
        const response = await apiClient.get('users/statuses');
        return response.data;
    }
);

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        resetCreateUserStatus: (state) => {
            state.createUserStatus = RequestStatus.Idle;
            state.currentUser = null;
        },
        resetUpdateUserStatus: (state) => {
            state.updateUserStatus = RequestStatus.Idle;
            state.currentUser = null;
        },
        resetDeleteUserStatus: (state) => {
            state.deleteUserStatus = RequestStatus.Idle;
            state.currentUser = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUsers.pending, (state) => {
                state.getUsersStatus = RequestStatus.Loading;
            })
            .addCase(getUsers.fulfilled, (state, action) => {
                state.getUsersStatus = RequestStatus.Success;
                state.users = action.payload;
            })
            .addCase(getUsers.rejected, (state, action) => {
                state.getUsersStatus = RequestStatus.Failed;
            })

            .addCase(createUser.pending, (state) => {
                state.createUserStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createUser.fulfilled, (state, action) => {
                state.createUserStatus = RequestStatus.Success;
            })
            .addCase(createUser.rejected, (state, action) => {
                state.createUserStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateUser.pending, (state) => {
                state.updateUserStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.updateUserStatus = RequestStatus.Success;
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.updateUserStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteUser.pending, (state) => {
                state.deleteUserStatus = RequestStatus.Loading;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.deleteUserStatus = RequestStatus.Success;
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.deleteUserStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentUser.pending, (state) => {
                state.getCurrentUserStatus = RequestStatus.Loading;
            })
            .addCase(getCurrentUser.fulfilled, (state, action) => {
                state.getCurrentUserStatus = RequestStatus.Success;
                state.currentUser = action.payload;
            })
            .addCase(getCurrentUser.rejected, (state, action) => {
                state.getCurrentUserStatus = RequestStatus.Failed;
            })

            .addCase(getStatuses.pending, (state) => {
                state.getUsersStatus = RequestStatus.Loading;
            })
            .addCase(getStatuses.fulfilled, (state, action) => {
                state.getUsersStatus = RequestStatus.Success;
                state.statuses = action.payload;
            })
            .addCase(getStatuses.rejected, (state, action) => {
                state.getUsersStatus = RequestStatus.Failed;
            })

            .addCase(updateStatus.pending, (state) => {
                state.updateUserStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateStatus.fulfilled, (state, action) => {

                const statuses = [...state.statuses].map(user => {
                    if (user.id === action.payload.user.id) {
                        return {
                            ...user,
                            offline: action.payload.user.offline
                        };
                    }
                    return user;
                });

                state.statuses = statuses;
                state.updateUserStatus = RequestStatus.Success;
            })
            .addCase(updateStatus.rejected, (state, action) => {
                state.updateUserStatus = RequestStatus.Failed;

                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            });
    },
});

export const {
    resetCreateUserStatus,
    resetUpdateUserStatus,
    resetDeleteUserStatus
} = usersSlice.actions;

export default usersSlice.reducer;